import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Popover, Typography } from 'seagull';
import { ArrowRight, Search } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useLocaleKeys } from '~/i18n';
import { normalizeVariableLabel } from '../TemplateVariables.utils';
import { useVariablePickerKeyboard } from './hooks/useVariablePickerKeyboard';
import styles from './VariablePickerPopover.module.scss';
import { buildSelectableItems, canCreateVariable, getCategoryForItem } from './VariablePickerPopover.utils';
export const GetVariablePickerPopoverTID = (parentTestId)=>getTestIds(parentTestId);
export function VariablePickerPopover(param) {
    let { anchorEl, customVariables, onSelect, onClose, excludeLabels = [], initialSearch = '', allowedSystemKeys, testId } = param;
    const { VariablePickerPopoverTestIds } = useMemo(()=>GetVariablePickerPopoverTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.templateVariables.picker');
    const history = useHistory();
    const [searchText, setSearchText] = useState(initialSearch);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const searchInputRef = useRef(null);
    const normalizedExcludeLabels = useMemo(()=>new Set(excludeLabels.map(normalizeVariableLabel)), [
        excludeLabels
    ]);
    const selectableItems = useMemo(()=>buildSelectableItems(searchText, customVariables, normalizedExcludeLabels, {
            allowedSystemKeys
        }), [
        searchText,
        customVariables,
        normalizedExcludeLabels,
        allowedSystemKeys
    ]);
    useEffect(()=>{
        setHighlightedIndex(0);
    }, [
        searchText,
        customVariables
    ]);
    useEffect(()=>{
        var _element_scrollIntoView;
        const element = document.getElementById("vpop-option-".concat(highlightedIndex));
        element === null || element === void 0 ? void 0 : (_element_scrollIntoView = element.scrollIntoView) === null || _element_scrollIntoView === void 0 ? void 0 : _element_scrollIntoView.call(element, {
            block: 'nearest'
        });
    }, [
        highlightedIndex
    ]);
    const canCreate = useMemo(()=>{
        if (!canCreateVariable(searchText, customVariables)) return false;
        const normalized = normalizeVariableLabel(searchText.trim());
        return !selectableItems.some((item)=>item.category !== 'Custom' && normalizeVariableLabel(item.label) === normalized);
    }, [
        searchText,
        customVariables,
        selectableItems
    ]);
    const focusSearchInput = useCallback(()=>{
        var _searchInputRef_current;
        (_searchInputRef_current = searchInputRef.current) === null || _searchInputRef_current === void 0 ? void 0 : _searchInputRef_current.focus();
    }, []);
    const handleCreate = useCallback(()=>{
        const trimmed = searchText.trim();
        if (trimmed) {
            onSelect(trimmed);
        }
    }, [
        searchText,
        onSelect
    ]);
    const handleKeyDown = useVariablePickerKeyboard({
        selectableItems,
        highlightedIndex,
        setHighlightedIndex,
        onSelect,
        onClose,
        canCreate,
        onCreate: handleCreate
    });
    return /*#__PURE__*/ _jsx(Popover, {
        open: !!anchorEl,
        anchorEl: anchorEl,
        onClose: onClose,
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: -4,
            horizontal: 'left'
        },
        disableAutoFocus: true,
        TransitionProps: {
            onEntered: focusSearchInput
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.popoverContent,
            "data-testid": VariablePickerPopoverTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.searchContainer,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.inputWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                size: 20,
                                className: styles.searchIcon
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                ref: searchInputRef,
                                type: "text",
                                className: styles.searchInput,
                                placeholder: t.searchPlaceholder(),
                                value: searchText,
                                onChange: (e)=>setSearchText(e.target.value),
                                onKeyDown: handleKeyDown,
                                "data-testid": VariablePickerPopoverTestIds.SearchInput(),
                                "aria-label": t.searchAriaLabel(),
                                "aria-controls": "vpop-listbox",
                                "aria-activedescendant": selectableItems.length > 0 ? "vpop-option-".concat(highlightedIndex) : undefined,
                                autoComplete: "off"
                            }),
                            canCreate && /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                className: styles.createAction,
                                onClick: handleCreate,
                                "data-testid": VariablePickerPopoverTestIds.CreateAction(),
                                children: [
                                    t.create(),
                                    /*#__PURE__*/ _jsx("kbd", {
                                        className: styles.kbdHint,
                                        children: "⏎"
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    id: "vpop-listbox",
                    className: styles.listContainer,
                    role: "listbox",
                    "aria-label": t.ariaLabel(),
                    children: selectableItems.map((item, index)=>{
                        const categoryHeader = getCategoryForItem(selectableItems, index);
                        return /*#__PURE__*/ _jsxs("div", {
                            children: [
                                categoryHeader && /*#__PURE__*/ _jsx("div", {
                                    className: styles.categoryHeader,
                                    "data-testid": VariablePickerPopoverTestIds.CategoryHeader(categoryHeader),
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        weight: "bold",
                                        skin: "secondary",
                                        children: categoryHeader
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    id: "vpop-option-".concat(index),
                                    className: "".concat(styles.variableItem, " ").concat(index === highlightedIndex ? styles.highlighted : ''),
                                    onClick: ()=>onSelect(item.label),
                                    "data-testid": VariablePickerPopoverTestIds.VariableItem(item.label),
                                    role: "option",
                                    "aria-selected": index === highlightedIndex,
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "light",
                                        skin: "standard",
                                        children: item.label
                                    })
                                })
                            ]
                        }, "".concat(item.category, "-").concat(item.label));
                    })
                }),
                /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    className: styles.manageTagsCta,
                    onClick: ()=>{
                        onClose();
                        history.push("".concat(routes.proposalDefaults, "#custom-tags"));
                    },
                    "data-testid": VariablePickerPopoverTestIds.ManageCustomTags(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.manageTagsContent,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    weight: "normal",
                                    skin: "primary",
                                    children: t.manageCustomTags()
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "light",
                                    children: t.manageCustomTagsDescription()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.manageTagsArrow,
                            children: /*#__PURE__*/ _jsx(ArrowRight, {})
                        })
                    ]
                })
            ]
        })
    });
}
